import React, { useState, useEffect } from "react";
import {
  Box, Text, SimpleGrid, VStack, HStack, Button,
  Badge, useColorModeValue, Icon, Progress,
  Divider, Heading, Flex, Spacer, Stat,
  StatLabel, StatNumber, StatHelpText,
} from "@chakra-ui/react";
import {
  FaBookOpen, FaClipboardList, FaFileAlt,
  FaPlay, FaChartBar, FaArrowRight,
  FaCheckCircle, FaClock, FaTrophy,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useStudentContext } from "../B2CStudentContext";
import axios from "axios";
import { openExamWindow } from "../../../utils/examWindow";
import  { useMemo } from "react";

/* ── Shared helper -- null/undefined = never attempted, 0 = real zero score ── */
const hasAttempted = (qs: any) =>
  qs.last_score_percentage !== null && qs.last_score_percentage !== undefined;

const B2CPracticeSetDashboard = () => {
  const [activeAttempt, setActiveAttempt] = useState<any>(null);
  const navigate = useNavigate();
  const { entitlement_id, practice_set_id } = useParams();
  const { studentContext } = useStudentContext();

  const cardBg = useColorModeValue("white", "gray.800");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const heroBg = useColorModeValue("blue.50", "blue.900");
  const heroBorder = useColorModeValue("blue.100", "blue.800");
  const resumeBg = useColorModeValue("orange.50", "orange.900");
  const resumeBorder = useColorModeValue("orange.200", "orange.700");
  const statBg = useColorModeValue("gray.50", "gray.750");

  const examTeaserExhaustedBg = useColorModeValue("red.50", "red.900");
const examTeaserActiveBg = useColorModeValue("purple.50", "purple.900");

const upgradeBg = useColorModeValue("green.50", "green.900");
const upgradeBorder = useColorModeValue("green.200", "green.700");

/*   const entitlement = studentContext?.entitlements.find(
    (e) => e.practice_set?.practice_set_id === Number(practice_set_id)
  ); */

// AFTER — uses entitlement_id from route, always unique:
const entitlement = useMemo(() => {
  return studentContext?.entitlements?.find(
    (e) => e.entitlement_id === Number(entitlement_id)
  );
}, [studentContext, entitlement_id]);

  const practiceSet = entitlement?.practice_set;

  const fetchActiveAttempt = async (entitlementId: number) => {
    try {
      const token = localStorage.getItem("b2c_token");
      const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
      const response = await axios.get(
        `${apiurl}/b2c/entitlement/${entitlementId}/active-attempt`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveAttempt(response.data);
    } catch {
      // no active attempt
    }
  };


  useEffect(() => {
    if (!entitlement?.entitlement_id) return;
    fetchActiveAttempt(entitlement.entitlement_id);
  }, [entitlement?.entitlement_id]);

  useEffect(() => {
    const entitlementId = entitlement?.entitlement_id;
    if (!entitlementId) return;
    const refresh = () => fetchActiveAttempt(entitlementId);
    const handleVisibilityChange = () => { if (!document.hidden) refresh(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [entitlement?.entitlement_id]);

  const ownsPaidInSameDomain = !!studentContext?.entitlements?.some(
  (e) =>
    e.entitlement_id !== entitlement?.entitlement_id &&
    !e.is_free &&
    !!e.domain_slug &&
    e.domain_slug === entitlement?.domain_slug
);

  if (!practiceSet) {
    return <Box p={10}><Text>Practice Set not found.</Text></Box>;
  }

  const totalQuestionSets = practiceSet.sections.reduce(
    (acc, section) =>
      acc + section.groups.reduce((g, group) => g + group.question_sets.length, 0),
    0
  );

  // Compute overall progress from last_score_percentage across all qsets
  const allQsets = practiceSet.sections.flatMap(s =>
    s.groups.flatMap(g => g.question_sets)
  );
 // const attemptedQsets = allQsets.filter(qs => (qs.last_score_percentage ?? 0) > 0);

    // ===== [ZERO-SCORE FIX] use hasAttempted (null/undefined check), not score > 0.
  // A real attempt that scores exactly 0% must still count as attempted --
  // `> 0` was silently excluding it, undercounting completed sets and
  // skewing average score. Same fix already applied in B2CSectionPractice
  // and B2CStudentAttemptListings; this brings the Dashboard in line. =====
  const attemptedQsets = allQsets.filter(hasAttempted);
  const overallProgress = totalQuestionSets > 0
    ? Math.round((attemptedQsets.length / totalQuestionSets) * 100)
    : 0;
  const avgScore = attemptedQsets.length > 0
    ? Math.round(
        attemptedQsets.reduce((sum, qs) => sum + (qs.last_score_percentage ?? 0), 0) /
        attemptedQsets.length
      )
    : null;

  const handleResumeAttempt = (practice_attempt_id: number) => {
    const url = `${window.location.origin}/mock-exam-window/${practice_attempt_id}`;
    window.location.href = url;
  };

  const sortedSections = [...practiceSet.sections].sort(
    (a, b) => a.sequence_number - b.sequence_number
  );




  return (
    <Box maxW="1400px" mx="auto" p={6}>

      {/* ── Resume banner -- only when active attempt exists ── */}
      {activeAttempt && (
        <Box bg={resumeBg} border="1px solid" borderColor={resumeBorder}
          borderRadius="xl" p={4} mb={6}>
          <Flex align="center">
            <HStack spacing={3}>
              <Icon as={FaClock} color="orange.500" boxSize={5} />
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="orange.700">
                  You have an attempt in progress
                </Text>
                <Text fontSize="xs" color="orange.600">
                  Pick up where you left off — your answers are saved.
                </Text>
              </Box>
            </HStack>
            <Spacer />
            <Button
              leftIcon={<FaPlay />}
              colorScheme="orange"
              size="sm"
              onClick={() => handleResumeAttempt(activeAttempt.practice_attempt_id)}
            >
              Resume
            </Button>
          </Flex>
        </Box>
      )}

      {/* ── Hero ── */}
      <Box bg={heroBg} border="1px solid" borderColor={heroBorder}
        borderRadius="2xl" p={8} mb={8}>
        <Flex align="start" gap={6} direction={{ base: "column", md: "row" }}>
          <VStack align="start" spacing={3} flex={1}>
            <Badge colorScheme="blue" px={3} py={1} borderRadius="full">
              {practiceSet.edition_label}
            </Badge>
            <Heading size="lg">{practiceSet.title}</Heading>
            <Text color={mutedText} lineHeight="1.7">
              {practiceSet.description}
            </Text>
            {entitlement?.end_date && (
              <HStack spacing={2}>
                <Icon as={FaClock} color={mutedText} boxSize={3} />
                <Text fontSize="xs" color={mutedText}>
                  Access until{" "}
                  {new Date(entitlement.end_date).toLocaleDateString("en-AU", {
                    day: "2-digit", month: "long", year: "numeric"
                  })}
                </Text>
              </HStack>
            )}
            <HStack spacing={3} pt={2}>
              <Button
                leftIcon={<FaPlay />}
                colorScheme="blue"
                onClick={() => {
                  const firstSection = sortedSections[0];
                  if (!firstSection) return;
                  navigate(
                    `/practice/app/entitlement/${entitlement.entitlement_id}/practice-set/${practiceSet.practice_set_id}/practice/${firstSection.practice_set_section_id}`
                  );
                }}
              >
                {attemptedQsets.length > 0 ? "Continue Practice" : "Start Practice"}
              </Button>
              <Button
                leftIcon={<FaChartBar />}
                variant="outline"
                colorScheme="blue"
                onClick={() =>
                  navigate(
                    `/practice/app/entitlement/${entitlement.entitlement_id}/practice-set/${practiceSet.practice_set_id}/attempts`
                  )
                }
              >
                My Progress
              </Button>
            </HStack>
          </VStack>

          {/* Progress ring summary */}
          <Box bg={cardBg} borderRadius="xl" p={5} minW="200px"
            borderWidth="1px" borderColor={borderColor} textAlign="center">
            <Text fontSize="xs" color={mutedText} mb={2} fontWeight="semibold"
              textTransform="uppercase" letterSpacing="wide">
              Overall Progress
            </Text>
            <Text fontSize="4xl" fontWeight="bold" color="blue.500">
              {overallProgress}%
            </Text>
            <Progress value={overallProgress} colorScheme="blue" size="sm"
              borderRadius="full" mt={2} mb={3} />
            <Text fontSize="xs" color={mutedText}>
              {attemptedQsets.length} of {totalQuestionSets} sets attempted
            </Text>
            {avgScore !== null && (
              <>
                <Divider my={3} />
                <HStack justify="center" spacing={1}>
                  <Icon as={FaTrophy} color="yellow.500" boxSize={3} />
                  <Text fontSize="sm" fontWeight="semibold">
                    Avg Score: {avgScore}%
                  </Text>
                </HStack>
              </>
            )}
          </Box>
        </Flex>
      </Box>

      {/* ── Upgrade prompt: free-only students ── */}
      {entitlement?.is_free && !ownsPaidInSameDomain && (
        <Box bg={upgradeBg} border="1px solid" borderColor={upgradeBorder}
          borderRadius="xl" p={5} mb={8}>
          <Flex align="center" gap={4} direction={{ base: "column", md: "row" }}>
            <HStack spacing={3} flex={1} align="start">
              <Icon as={FaTrophy} color="green.500" boxSize={5} mt="2px" flexShrink={0} />
              <Box>
                <Text fontWeight="bold" fontSize="sm">Unlock full access</Text>
                <Text fontSize="sm" color={mutedText}>
                  You're on free access
                  {entitlement.end_date && (
                    <> that ends on{" "}
                      {new Date(entitlement.end_date).toLocaleDateString("en-AU", {
                        day: "2-digit", month: "long", year: "numeric",
                      })}
                    </>
                  )}
                  . See the plans available to continue your preparation.
                </Text>
              </Box>
            </HStack>
            <Button colorScheme="green" rightIcon={<FaArrowRight />}
              flexShrink={0} onClick={() =>
                navigate(
                  entitlement.domain_slug
                    ? `/practice/${entitlement.domain_slug}#paid`
                    : "/practice"
                )
              }>
              See Plans
            </Button>
          </Flex>
        </Box>
      )}

      {/* ── Stats strip ── */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={8}>
        {[
          {
            label: "Sections",
            value: practiceSet.sections.length,
            icon: FaBookOpen,
            color: "blue",
          },
          {
            label: "Question Sets",
            value: totalQuestionSets,
            icon: FaClipboardList,
            color: "teal",
          },
          {
            label: "Sets Attempted",
            value: attemptedQsets.length,
            icon: FaCheckCircle,
            color: "green",
          },
          {
            label: "Mock Exams",
            value: practiceSet.exams.length,
            icon: FaFileAlt,
            color: "purple",
          },
        ].map((stat) => (
          <Box key={stat.label} bg={cardBg} borderRadius="xl"
            borderWidth="1px" borderColor={borderColor} p={4}>
            <HStack spacing={3}>
              <Box bg={`${stat.color}.50`} p={2} borderRadius="lg">
                <Icon as={stat.icon} color={`${stat.color}.500`} boxSize={4} />
              </Box>
              <Box>
                <Text fontSize="xs" color={mutedText}>{stat.label}</Text>
                <Text fontSize="xl" fontWeight="bold">{stat.value}</Text>
              </Box>
            </HStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* ── Practice Sections ── */}
      <VStack align="stretch" spacing={5} mb={8}>
        <Flex align="center">
          <Heading size="md">Practice Sections</Heading>
          <Spacer />
          <Badge colorScheme="blue" px={3} py={1} borderRadius="full">
            {practiceSet.sections.length} sections
          </Badge>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
          {sortedSections.map((section) => {
            const totalSets = section.groups.reduce(
              (acc, group) => acc + group.question_sets.length, 0
            );
            const sectionQsets = section.groups.flatMap(g => g.question_sets);
            // ===== [ZERO-SCORE FIX] same hasAttempted fix as the hero section,
            // applied here since each section card computes its own progress
            // independently rather than reusing the hero's attemptedQsets. =====
            const attempted = sectionQsets.filter(hasAttempted).length;
            const progress = totalSets > 0
              ? Math.round((attempted / totalSets) * 100)
              : 0;
            const inProgress = sectionQsets.some(
              qs => qs.is_attempt_in_progress
            );

            const progressColor =
              progress === 100 ? "green" :
              progress > 50 ? "blue" :
              progress > 0 ? "orange" : "gray";

            return (
              <Box key={section.practice_set_section_id} bg={cardBg}
                borderRadius="2xl" borderWidth="1px"
                borderColor={inProgress ? "orange.200" : borderColor}
                p={6} position="relative"
                transition="box-shadow 0.2s"
                _hover={{ boxShadow: "md" }}>

                {inProgress && (
                  <Badge position="absolute" top={3} right={3}
                    colorScheme="orange" fontSize="2xs" borderRadius="full" px={2}>
                    In Progress
                  </Badge>
                )}

                {progress === 100 && (
                  <Badge position="absolute" top={3} right={3}
                    colorScheme="green" fontSize="2xs" borderRadius="full" px={2}>
                    Complete
                  </Badge>
                )}

                <VStack align="start" spacing={4}>
                  <HStack spacing={3}>
                    <Icon as={FaClipboardList} color="blue.500" />
                    <Text fontWeight="bold" fontSize="md" pr={16}>
                      {section.title}
                    </Text>
                  </HStack>

                  <Text color={mutedText} fontSize="sm">
                    {section.groups.length} groups · {totalSets} question sets
                  </Text>

                  <Box w="100%">
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="xs" color={mutedText}>Progress</Text>
                      <Text fontSize="xs" fontWeight="semibold"
                        color={`${progressColor}.500`}>
                        {attempted}/{totalSets}
                      </Text>
                    </Flex>
                    <Progress value={progress} size="sm" colorScheme={progressColor}
                      borderRadius="full" />
                  </Box>

                  <Button width="100%" colorScheme="blue" variant="outline"
                    rightIcon={<FaArrowRight />}
                    onClick={() =>
                      navigate(
                        `/practice/app/entitlement/${entitlement.entitlement_id}/practice-set/${practiceSet.practice_set_id}/practice/${section.practice_set_section_id}`
                      )
                    }>
                    {progress === 0 ? "Start Section" :
                     progress === 100 ? "Review Section" : "Continue Section"}
                  </Button>
                </VStack>
              </Box>
            );
          })}
        </SimpleGrid>
      </VStack>

      {/* ── Mock Exams teaser -- links to dedicated page ── */}
      {practiceSet.exams.length > 0 && (
        <Box bg={cardBg} borderRadius="2xl" borderWidth="1px"
          borderColor={borderColor} p={6}>
          <Flex align="center" mb={4}>
            <HStack spacing={3}>
              <Icon as={FaFileAlt} color="purple.500" boxSize={5} />
              <Heading size="md">Mock Exams</Heading>
              <Badge colorScheme="purple" borderRadius="full" px={2}>
                {practiceSet.exams.length} available
              </Badge>
            </HStack>
            <Spacer />
            <Button size="sm" colorScheme="purple" variant="outline"
              rightIcon={<FaArrowRight />}
              onClick={() =>
                navigate(
                  `/practice/app/entitlement/${entitlement.entitlement_id}/practice-set/${practiceSet.practice_set_id}/mock-exams`
                )
              }>
              View All Exams
            </Button>
          </Flex>

          <Text fontSize="sm" color={mutedText} mb={4}>
            Full-length timed exams simulating real exam conditions. Attempt these
            once you have completed your practice sections for best results.
          </Text>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
            {[...practiceSet.exams]
              .sort((a, b) => a.sequence_number - b.sequence_number)
              .slice(0, 3)
              .map((exam, idx) => {
                const exhausted = exam.max_attempts != null &&
                  (exam.remaining_attempts ?? 0) <= 0;
                return (
                  <HStack key={exam.practice_set_exam_id} p={3} borderRadius="lg"
                    borderWidth="1px"
                    borderColor={exhausted ? "red.200" : "purple.100"}
                    bg={exhausted
                      ? examTeaserExhaustedBg
                      : examTeaserActiveBg}
                    justify="space-between">
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                        {exam.title}
                      </Text>
                      <Text fontSize="xs" color={mutedText}>
                        {exam.total_duration_minutes} mins
                      </Text>
                    </VStack>
                    <Badge colorScheme={exhausted ? "red" : "purple"} fontSize="2xs">
                      {exhausted ? "Done" :
                       exam.max_attempts == null ? "∞" :
                       `${exam.remaining_attempts} left`}
                    </Badge>
                  </HStack>
                );
              })}
          </SimpleGrid>
        </Box>
      )}

    </Box>
  );
};

export default B2CPracticeSetDashboard;